'use client';

import { fetchGoodsList } from '@/api/product';
import type {
  IProductListApiParams,
  IProductListApiResponse,
  IProductListItem,
} from '@/api/types/product';
import GoodsFilter from '@/app/goods-list/components/GoodsFilter';
import ProductList from '@/app/goods-list/components/ProductList';
import SortFilterBar from '@/app/goods-list/components/SortFilterBar';
import NavigationSection from '@/app/(activity)/campaigns/sunglasses-sale/components/NavigationSection';
import {
  getDefaultPcSecondaryLocation,
  useNavigation,
} from '@/app/(activity)/campaigns/sunglasses-sale/hooks/useNavigation';
import Popup from '@/components/Popup';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { DEFAULT_SORT_VALUE } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { getHtmlText, transformFilterParams } from '@/utils/goods-list-utils';
import { versionCompare } from '@/utils/tools';
import ExpandableText from '@/components/ExpandableText';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CampaignBanner from '../../components/CampaignBanner';
import { useCampaignPageTracking } from '../../hooks/useCampaignPageTracking';
import { useUserIdentityChange } from '../../hooks/useUserIdentityChange';
import type { CampaignsServerData } from '../../types';
import { resolveGuideProductPoolInfo } from '../../utils/guideProductPoolInfo';

const PAGE_SIZE = 36;
const GUIDE_DEFAULT_GROUP_ID = 81;

interface GuidePageProps {
  serverData: CampaignsServerData;
  isAppCommunicationReady: boolean;
  inAppH5: boolean;
  onBottomContentReady?: () => void;
}

export default function GuidePage({
  serverData,
  isAppCommunicationReady,
  inAppH5,
  onBottomContentReady,
}: GuidePageProps) {
  const guideData = serverData.guideData;
  const deviceType = serverData.deviceType as EPlatform;
  const isWeb = deviceType === EPlatform.WEB;
  const config = guideData?.pageConfig;
  const currency = useStore((state) => state.currency);
  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
  const publicResourceData = useStore((state) => state.publicResourceData);
  const searchParams = useSearchParams();
  const t = useTranslations('goodsList');

  const [activeGroupId, setActiveGroupId] = useState(
    guideData?.currentGroupId || GUIDE_DEFAULT_GROUP_ID,
  );
  const [listData, setListData] = useState<IProductListItem[]>(guideData?.products || []);
  const [productGroup, setProductGroup] = useState(guideData?.group);
  const [total, setTotal] = useState(guideData?.total || 0);
  const [allTotal, setAllTotal] = useState(guideData?.allTotal || 0);
  const [imageRule, setImageRule] = useState(guideData?.imageRule);
  const [imageHoverRule, setImageHoverRule] = useState(guideData?.hoverImageRule);
  const [noData, setNoData] = useState((guideData?.products || []).length === 0);
  const [loading, setLoading] = useState(false);
  const [noMoreShow, setNoMoreShow] = useState((guideData?.allTotal || 0) <= PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [infiniteScrollPage, setInfiniteScrollPage] = useState(1);
  const [column, setColumn] = useState(2);
  const [currentSort, setCurrentSort] = useState(DEFAULT_SORT_VALUE);
  const [currentFilter, setCurrentFilter] = useState<Record<string, unknown>>({});
  const [tempFilter, setTempFilter] = useState<Record<string, unknown>>({});
  const [showFilter, setShowFilter] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [stickyNavVisible, setStickyNavVisible] = useState(false);
  const [showTryOn, setShowTryOn] = useState(true);

  const {
    trackPageView,
    trackPageLeave,
    trackNavTabClick,
    trackProductListPitsClick,
    trackProductListPitsExposure,
  } = useCampaignPageTracking(serverData.runtimeConfig);

  const nav = useNavigation({
    navigation: (config?.navigation[isWeb ? 'web' : 'wap'] || []) as any,
    defaultGroupId: guideData?.currentGroupId || GUIDE_DEFAULT_GROUP_ID,
    onNavigationChange: (groupId) => {
      setActiveGroupId(groupId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });
  const navKey = `${nav.primaryIndex}-${isWeb ? nav.pcSelectedSecondary[nav.primaryIndex] : nav.secondaryIndex}`;
  const prevNavKeyRef = useRef(navKey);
  const latestProductRequestIdRef = useRef(0);

  useEffect(() => {
    if (!isAppCommunicationReady) return;

    trackPageView();

    if (inAppH5) {
      window.addEventListener('appView', trackPageView);
      window.addEventListener('appLeave', trackPageLeave);
    }

    return () => {
      trackPageLeave();
      if (inAppH5) {
        window.removeEventListener('appView', trackPageView);
        window.removeEventListener('appLeave', trackPageLeave);
      }
    };
  }, [inAppH5, isAppCommunicationReady, trackPageLeave, trackPageView]);

  useEffect(() => {
    if (!inAppH5) return;
    const version = searchParams.get('version') || '';
    const moreThan = version ? versionCompare(version, '3.2.55', '>') : false;
    setShowTryOn(moreThan);
  }, [inAppH5, searchParams]);

  useEffect(() => {
    const productFilterNew = publicResourceData?.config_names?.product_filter_new || [];
    if (productFilterNew.length === 0) return;

    const initialState: Record<string, boolean> = {};
    productFilterNew.forEach((item) => {
      initialState[item.attribute_code] = true;
    });
    setExpandedSections(initialState);
  }, [publicResourceData]);

  useEffect(() => {
    if (!isWeb && noMoreShow) {
      setIsShowFooterNav(true);
    }
  }, [isWeb, noMoreShow, setIsShowFooterNav]);

  useEffect(() => {
    if (isAppCommunicationReady) {
      onBottomContentReady?.();
    }
  }, [isAppCommunicationReady, onBottomContentReady]);

  const filterTotal = useMemo(() => {
    return Object.keys(currentFilter).reduce((acc, key) => {
      if (['temple', 'frame_width'].includes(key)) return acc;
      const value = currentFilter[key];
      if (Array.isArray(value)) return acc + value.length;
      return value ? acc + 1 : acc;
    }, 0);
  }, [currentFilter]);

  const selectedSecondaryIndex = isWeb
    ? nav.pcSelectedSecondary[nav.primaryIndex]
    : nav.secondaryIndex >= 0
      ? nav.secondaryIndex
      : null;

  const currentSecondary = useMemo(() => {
    if (selectedSecondaryIndex === null || selectedSecondaryIndex === undefined) {
      return nav.currentPrimary?.secondaryNav.find((item) => item.isDefault);
    }
    return nav.currentPrimary?.secondaryNav[selectedSecondaryIndex];
  }, [nav.currentPrimary, selectedSecondaryIndex]);

  const currentTabName = currentSecondary?.label || nav.currentPrimary?.label || '';
  const currentProductPoolInfo = useMemo(
    () =>
      resolveGuideProductPoolInfo({
        group: productGroup,
        fallbackTitle: currentSecondary?.productPoolTitle || nav.currentPrimary?.label,
      }),
    [currentSecondary?.productPoolTitle, nav.currentPrimary?.label, productGroup],
  );
  const currentProductPoolTitle = currentProductPoolInfo.title;
  const currentProductPoolSubtitle = currentProductPoolInfo.subtitle;
  const pcSecondaryBanner =
    isWeb && currentSecondary && !currentSecondary.isDefault
      ? currentSecondary.bannerImage || nav.currentPrimary?.bannerImage
      : undefined;

  const stickyNavTop = useMemo(() => {
    if (inAppH5) {
      return stickyNavVisible ? '!top-[25.6vw]' : '!top-0';
    }
    if (isWeb) {
      return stickyNavVisible ? '2xl:top-[164px] top-[204px]' : 'top-[100px]';
    }
    return stickyNavVisible ? 'top-[37.333vw]' : 'top-[11.733vw]';
  }, [stickyNavVisible, inAppH5, isWeb]);

  const fetchProducts = useCallback(
    async (
      groupId: number,
      page: number,
      sort: string,
      filter: Record<string, unknown>,
      append = false,
    ) => {
      if (!currency?.name) return;

      const requestId = latestProductRequestIdRef.current + 1;
      latestProductRequestIdRef.current = requestId;
      const isLatestRequest = () => latestProductRequestIdRef.current === requestId;

      setLoading(true);
      if (!isWeb) setIsShowFooterNav(false);

      try {
        let sortParam = sort;
        if (sortParam === '' || sortParam === DEFAULT_SORT_VALUE) sortParam = '';
        const transformedFilter = transformFilterParams(filter);
        const apiParams: IProductListApiParams = {
          group_id: groupId,
          currency: currency.name,
          limit: PAGE_SIZE,
          page,
          order: sortParam,
          in_stock: 1,
          is_sale: 1,
          filter: transformedFilter,
          append_correlation: 1,
          show_adv: 1,
        };

        const response = (await fetchGoodsList(apiParams)) as unknown as {
          code: number;
          data: IProductListApiResponse;
        };
        if (!isLatestRequest()) return;

        if (response.code === 200 && response.data) {
          const newList = response.data.list || [];
          const newTotal = response.data.total || 0;
          const newAllTotal = response.data.all_total || 0;

          setListData((prev) => (append ? [...prev, ...newList] : newList));
          if (!append && response.data.group) {
            setProductGroup(response.data.group);
          }
          setTotal(newTotal);
          setAllTotal(newAllTotal);
          setNoData(newList.length === 0 && page === 1);
          setNoMoreShow(newAllTotal <= page * PAGE_SIZE);
          setImageRule(response.data.image_rule);
          setImageHoverRule(response.data.hover_image_rule);
        }
      } catch (error) {
        if (!isLatestRequest()) return;

        logger.error('Failed to fetch guide products:', error);
        if (page === 1) setNoData(true);
      } finally {
        if (isLatestRequest()) {
          setLoading(false);
          setIsGlobalLoading(false);
        }
      }
    },
    [currency?.name, isWeb, setIsGlobalLoading, setIsShowFooterNav],
  );

  useEffect(() => {
    if (prevNavKeyRef.current === navKey) return;
    prevNavKeyRef.current = navKey;

    setCurrentPage(1);
    setInfiniteScrollPage(1);
    setCurrentSort(DEFAULT_SORT_VALUE);
    setCurrentFilter({});
    setTempFilter({});
    setNoMoreShow(false);
    setIsGlobalLoading(true);

    fetchProducts(activeGroupId, 1, DEFAULT_SORT_VALUE, {});
  }, [activeGroupId, fetchProducts, navKey, setIsGlobalLoading]);

  const refreshProductsForIdentityChange = useCallback(() => {
    setCurrentPage(1);
    setInfiniteScrollPage(1);
    setIsGlobalLoading(true);
    fetchProducts(activeGroupId, 1, currentSort, currentFilter);
  }, [activeGroupId, currentFilter, currentSort, fetchProducts, setIsGlobalLoading]);

  useUserIdentityChange(refreshProductsForIdentityChange, isAppCommunicationReady, {
    refreshOnEnable: inAppH5,
  });

  const scrollToBelowNav = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSortChange = useCallback(
    (sortValue: { value: string | number }) => {
      const newSort = sortValue.value.toString();
      setCurrentSort(newSort);
      setCurrentPage(1);
      setInfiniteScrollPage(1);
      setIsGlobalLoading(true);
      scrollToBelowNav();
      fetchProducts(activeGroupId, 1, newSort, currentFilter);
    },
    [activeGroupId, currentFilter, fetchProducts, scrollToBelowNav, setIsGlobalLoading],
  );

  const handleFilterChange = useCallback(
    (newFilter: Record<string, unknown>) => {
      setCurrentFilter(newFilter);
      setTempFilter(newFilter);
      setCurrentPage(1);
      setInfiniteScrollPage(1);
      setIsGlobalLoading(true);
      scrollToBelowNav();
      fetchProducts(activeGroupId, 1, currentSort, newFilter);
    },
    [activeGroupId, currentSort, fetchProducts, scrollToBelowNav, setIsGlobalLoading],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      setIsGlobalLoading(true);
      scrollToBelowNav();
      fetchProducts(activeGroupId, page, currentSort, currentFilter);
    },
    [
      activeGroupId,
      currentFilter,
      currentSort,
      fetchProducts,
      scrollToBelowNav,
      setIsGlobalLoading,
    ],
  );

  const handleReachBottom = useCallback(async () => {
    if (isWeb || loading || noMoreShow) return;

    const nextPage = infiniteScrollPage + 1;
    setInfiniteScrollPage(nextPage);
    await fetchProducts(activeGroupId, nextPage, currentSort, currentFilter, true);
  }, [
    activeGroupId,
    currentFilter,
    currentSort,
    fetchProducts,
    infiniteScrollPage,
    isWeb,
    loading,
    noMoreShow,
  ]);

  const handleFilterClick = useCallback(() => {
    setTempFilter(currentFilter);
    setShowFilter(true);
  }, [currentFilter]);

  const handleFilterClose = useCallback(() => {
    setTempFilter(currentFilter);
    setShowFilter(false);
  }, [currentFilter]);

  const handleFilterApply = useCallback(() => {
    handleFilterChange(tempFilter);
    setShowFilter(false);
  }, [handleFilterChange, tempFilter]);

  const handleFilterValueChange = useCallback(
    (filterId: string, value: unknown, isPopup: boolean) => {
      if (isPopup) {
        setTempFilter((prev) => ({ ...prev, [filterId]: value }));
        return;
      }
      handleFilterChange({ ...(currentFilter || {}), [filterId]: value });
    },
    [currentFilter, handleFilterChange],
  );

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  const handleColumnChange = useCallback(
    (newColumn: number) => {
      setColumn(newColumn);
      if (listData.length > 0) scrollToBelowNav();
    },
    [listData.length, scrollToBelowNav],
  );

  const renderGoodsFilter = useCallback(
    (isPopup = false) => {
      const filterValues = isPopup ? tempFilter : currentFilter;
      return (
        <GoodsFilter
          values={filterValues}
          onValueChange={(filterId, value) => handleFilterValueChange(filterId, value, isPopup)}
          onClear={() => {
            if (isPopup) setTempFilter({});
            else handleFilterChange({});
          }}
          isWeb={isWeb}
          expandedSections={expandedSections}
          onToggleSection={toggleSection}
          isPopup={isPopup}
          {...(isPopup ? { onApply: handleFilterApply } : {})}
        />
      );
    },
    [
      currentFilter,
      expandedSections,
      handleFilterApply,
      handleFilterChange,
      handleFilterValueChange,
      isWeb,
      tempFilter,
      toggleSection,
    ],
  );

  if (!config || !isAppCommunicationReady) {
    return null;
  }

  return (
    <div className="min-h-screen">
      <NavigationSection
        isWeb={isWeb}
        deviceType={deviceType}
        navigation={config.navigation as any}
        onStickyNavChange={setStickyNavVisible}
        stickyNavVisible={stickyNavVisible}
        primaryIndex={nav.primaryIndex}
        pcSelectedSecondary={nav.pcSelectedSecondary}
        onPrimaryClick={(index) => {
          nav.selectPrimary(index);
          if (!isWeb) {
            const primary = config.navigation.wap[index];
            const defaultIdx = Math.max(
              0,
              primary?.secondaryNav?.findIndex((item) => item.isDefault) ?? 0,
            );
            const globalIdx = config.navigation.wap
              .slice(0, index)
              .reduce((acc, item) => acc + (item?.secondaryNav?.length || 0), defaultIdx);
            trackNavTabClick(
              primary?.secondaryNav[defaultIdx]?.label || primary?.label || '',
              globalIdx,
            );
          }
        }}
        onSecondaryToggle={(pIndex, sIdx) => {
          const primary = config.navigation.web[pIndex];
          const currentSelected = nav.pcSelectedSecondary[pIndex];
          nav.toggleSecondary(pIndex, sIdx);
          const offset = config.navigation.web
            .slice(0, pIndex)
            .reduce((acc, item) => acc + (item?.secondaryNav?.length || 0), 0);
          if (currentSelected === sIdx) {
            const defaultLocation = getDefaultPcSecondaryLocation(config.navigation.web as any);
            const defaultOffset = config.navigation.web
              .slice(0, defaultLocation?.primaryIndex ?? 0)
              .reduce((acc, item) => acc + (item?.secondaryNav?.length || 0), 0);
            const targetNav =
              defaultLocation &&
              config.navigation.web[defaultLocation.primaryIndex]?.secondaryNav[
                defaultLocation.secondaryIndex
              ];
            trackNavTabClick(
              targetNav?.label || '',
              defaultOffset + (defaultLocation?.secondaryIndex ?? 0),
            );
            return;
          }
          trackNavTabClick(primary?.secondaryNav[sIdx]?.label || '', offset + sIdx);
        }}
        selectedBannerImage={pcSecondaryBanner}
        renderHeroBanner={({ image, alt }) => (
          <CampaignBanner
            data={{
              image: image || '',
              image_alt: alt,
            }}
            aspectRatio="1920/560"
            className="w-full lg:min-h-[358px] min-h-[398px] overflow-hidden aspect-[1920/560]"
            imageClassName="w-full h-full lg:min-h-[358px] min-h-[398px] overflow-hidden object-cover aspect-[1920/560]"
            width={1920}
            height={560}
          />
        )}
        secondaryIndex={nav.secondaryIndex}
        onSecondaryClick={(sIdx) => {
          const tabLabel = nav.currentPrimary?.secondaryNav[sIdx]?.label || '';
          const offset = config.navigation.wap
            .slice(0, nav.primaryIndex)
            .reduce((acc, item) => acc + (item?.secondaryNav?.length || 0), 0);
          trackNavTabClick(tabLabel, offset + sIdx);
          nav.selectSecondary(sIdx);
        }}
      />

      <div className={`relative ${isWeb ? 'w-full min-h-screen flex pt-[30px]' : ''}`}>
        {isWeb && (
          <div className="max-[920px]:hidden pt-[4px]">
            <div
              className={`w-[304px] left-0 z-[2] h-screen sticky transition-[top] duration-300 ease-in-out ${stickyNavTop}`}
            >
              <div className="h-full bg-white overflow-y-auto show-scrollbar-when-hover">
                <p className="pt-[16px] text-20 leading-[24px] font-bb pb-24 pl-48">
                  {t('Filters')}
                </p>
                <div className="pl-48 pr-24 pb-[350px]">{renderGoodsFilter(false)}</div>
              </div>
            </div>
          </div>
        )}

        <div
          className={`relative ${isWeb ? 'min-h-screen w-[calc(100vw-304px)] max-[920px]:w-full' : 'w-full'}`}
        >
          <div className={isWeb ? 'px-40 pb-40' : ''}>
            {(currentProductPoolTitle || currentProductPoolSubtitle) && (
              <div
                className={`banner-down-area ${
                  isWeb
                    ? 'pt-20 pb-[24px] border-b border-solid border-[#eeeeee]'
                    : 'pt-[18px] pb-[12px]'
                }`}
              >
                {currentProductPoolTitle && (
                  <h2
                    className={`banner-title font-bb text-[#282828] ${
                      isWeb ? 'text-32 leading-[38px]' : 'text-[16px] leading-[19px] px-[14px]'
                    }`}
                    dangerouslySetInnerHTML={{ __html: getHtmlText(currentProductPoolTitle) }}
                  />
                )}
                {currentProductPoolSubtitle && (
                  <h3
                    className={`banner-desc-wrapper text-[#282828] ${
                      isWeb ? 'mt-[15px] max-w-[750px]' : 'mt-[6px] px-[14px]'
                    }`}
                  >
                    <ExpandableText
                      text={currentProductPoolSubtitle}
                      maxLines={1}
                      className={`${isWeb ? 'text-14 leading-[22px]' : 'text-12 leading-[18px]'} font-ir`}
                      isWeb={isWeb}
                      textClassName="max-w-[750px]"
                    />
                  </h3>
                )}
              </div>
            )}

            <SortFilterBar
              wrapperClassName={`transition-[top] duration-300 ease-in-out ${stickyNavTop} pt-[4px]`}
              currentSort={currentSort}
              onSortChange={handleSortChange}
              filterTotal={filterTotal}
              onFilterClick={handleFilterClick}
              showColumnControl={!isWeb}
              column={column}
              onColumnChange={handleColumnChange}
              isWeb={isWeb}
              total={total}
              groupInfo={{ filter_show: 1 }}
              onSortBtnClick={() => {}}
            />

            <section className="guide-list-section">
              <ProductList
                showTryOn={showTryOn}
                listData={listData}
                total={total}
                allTotal={allTotal}
                noData={noData}
                isWeb={isWeb}
                column={column}
                isAcc={false}
                imageRule={imageRule}
                imageHoverRule={imageHoverRule}
                infiniteScrollPage={infiniteScrollPage}
                limit={PAGE_SIZE}
                handleReachBottom={handleReachBottom}
                page={currentPage}
                handlePageChange={handlePageChange}
                currentFilter={currentFilter}
                handleProductClick={(index: number, item: any) => {
                  trackProductListPitsClick(item, index, '商品版位', currentTabName);
                }}
                handleProductInView={() => {}}
                handleProductValidInView={(index: number, item: any) => {
                  trackProductListPitsExposure(item, index, '商品版位', currentTabName);
                }}
                loading={loading}
                SDProductListPageTryon_Click={() => {}}
                sdProductListPageADExp={() => {}}
                sdProductListPageADClick={() => {}}
              />
            </section>
          </div>
        </div>
      </div>

      {isWeb && (
        <Popup
          open={showFilter}
          onClose={handleFilterClose}
          position="left"
          width="482px"
          round={false}
          title={t('Filters')}
          bodyClassName="!pt-0 !px-32"
        >
          {renderGoodsFilter(true)}
        </Popup>
      )}

      {!isWeb && (
        <Popup
          open={showFilter}
          onClose={handleFilterClose}
          position="bottom"
          title={t('Filters')}
          bodyClassName="!pt-0 !px-16"
        >
          {renderGoodsFilter(true)}
        </Popup>
      )}
    </div>
  );
}
